import * as React from 'react';
import Button from './Button';

interface TimerButtonProps extends React.ComponentProps<typeof Button> {
  initialSeconds?: number;
  onTimerComplete?: () => void;
}

const TICK_MS = 250;
const DEFAULT_SECONDS = 30;

const secondsUntil = (targetAt: number) =>
  Math.max(0, Math.ceil((targetAt - Date.now()) / 1000));

const TimerButton: React.FC<TimerButtonProps> = ({
  initialSeconds = DEFAULT_SECONDS,
  onTimerComplete,
  children,
  ...buttonProps
}) => {
  const [targetAt] = React.useState(() => Date.now() + Math.max(0, initialSeconds) * 1000);
  const [secondsLeft, setSecondsLeft] = React.useState(() => secondsUntil(targetAt));

  const onTimerCompleteRef = React.useRef(onTimerComplete);
  React.useEffect(() => { onTimerCompleteRef.current = onTimerComplete; });

  React.useEffect(() => {
    if (secondsLeft <= 0) {
      onTimerCompleteRef.current?.();
      return;
    }

    const id = window.setInterval(() => {
      const remaining = secondsUntil(targetAt);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        window.clearInterval(id);
        onTimerCompleteRef.current?.();
      }
    }, TICK_MS);

    return () => window.clearInterval(id);
  }, [targetAt]);

  const isCountingDown = secondsLeft > 0;

  return (
    <Button {...buttonProps} disabled={buttonProps.disabled || isCountingDown}>
      {children}{isCountingDown && ` (${secondsLeft}s)`}
    </Button>
  );
};

export default TimerButton;
